import { useEffect, useLayoutEffect } from 'react';

import { isBananaCombinedResults } from '@web-platform/shared-banana-utils-frontend/src/helpers/isBananaCombinedResults';
import { PAGE_GROUPS } from '@web-platform/shared-universal-constants/src/pageGroups';
import { pageGroupChanged } from '@web-platform/shared-universal-redux/src/actions/pageGroup';
import {
  getDestination,
  getOrigin,
  getOutboundDate,
} from '@web-platform/shared-universal-redux/src/selectors/search';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { sendToNewRelic } from '@web-platform/shared-universal-utils-frontend/src/helpers/newrelic/sendToNewRelic';
import { useDispatch, useSelector } from 'react-redux';
import { matchRoutes, useLocation } from 'react-router-dom';
import { GLOBAL } from 'saddlebag-browser';

import type { RouteType } from '../types';
import type { PageGroup } from '@web-platform/shared-universal-constants/src/pageGroups';
import type { ContextSearchPlace } from '@web-platform/shared-universal-types/src/context';
import type { DateString } from '@web-platform/shared-universal-types/src/dateString';
import type { Location } from 'react-router-dom';

const getPageGroupName = (
  location: Location,
  routes: RouteType[],
  destination: ContextSearchPlace | undefined,
  origin: ContextSearchPlace | undefined,
  outboundDate: DateString | undefined,
): PageGroup => {
  const matchedRoutes = matchRoutes(routes, location.pathname);
  let pageGroupName = matchedRoutes?.[0]?.route?.pageGroupName || PAGE_GROUPS.UNKNOWN_PAGE_GROUP;

  if (pageGroupName === PAGE_GROUPS.COMBINED_ENTRY_PAGE) {
    if (location.hash.includes('details')) {
      pageGroupName = PAGE_GROUPS.BOOKING_PANEL;
    } else {
      const isCombinedResults = isBananaCombinedResults(destination, origin, outboundDate);
      pageGroupName = isCombinedResults
        ? PAGE_GROUPS.COMBINED_RESULTS
        : PAGE_GROUPS.COMBINED_EXPLORE;
    }
  }

  return pageGroupName;
};

export const useNewRelicCustomAttributes = (routes: RouteType[]) => {
  const $window = GLOBAL.getWindow();
  const location = useLocation();
  const destination = useSelector(getDestination);
  const origin = useSelector(getOrigin);
  const outboundDate = useSelector(getOutboundDate);
  const dispatch = useDispatch();

  const pageGroupName = getPageGroupName(location, routes, destination, origin, outboundDate);

  useEffect(() => {
    try {
      sendToNewRelic('fullUrl', $window.location.href, $window);
    } catch (err) {
      logError({
        component: 'useNewRelicCustomAttributes',
        message: 'unable to update NR custom attributes fullUrl',
        error: err,
      });
    }
    // eslint suggests to add $window for completeness but (even though that should be stable) it is only href changing needs to trigger event
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$window.location.href]);

  useLayoutEffect(() => {
    try {
      sendToNewRelic('pageGroup', pageGroupName, GLOBAL.getWindow());
      dispatch(pageGroupChanged(pageGroupName));
    } catch (err) {
      logError({
        component: 'useNewRelicCustomAttributes',
        message: 'unable to update NR custom attributes pageGroup',
        error: err,
      });
    }
  }, [pageGroupName, dispatch]);
};
